
import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { settingsApi } from '@/integrations/mysql/client';
import {
  CHANNEL_META, DEFAULT_CHANNELS,
  type ContactChannel,
} from '@/components/ContatoManager';

function parseChannels(raw: string | undefined): ContactChannel[] {
  if (!raw) return DEFAULT_CHANNELS;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) && p.length > 0 ? p : DEFAULT_CHANNELS;
  } catch { return DEFAULT_CHANNELS; }
}

export function ContactCards() {
  const [channels, setChannels] = useState<ContactChannel[]>(DEFAULT_CHANNELS);

  useEffect(() => {
    settingsApi.list().then(list => {
      const raw = list.find(s => s.key === 'contact_channels_config')?.value;
      setChannels(parseChannels(raw));
    }).catch(() => {});
  }, []);

  const visible = channels.filter(c => c.enabled && c.value);

  return (
    <div className="flex flex-col gap-3">
      {visible.map(channel => {
        const meta = CHANNEL_META[channel.type];
        const Icon = meta.icon;
        const href = meta.buildHref(channel.value);
        const isExternal = href.startsWith('http') || href.startsWith('mailto') || href.startsWith('tel');

        return (
          <div key={channel.id}
            className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 p-5 flex items-center gap-4">
            <div className={`w-12 h-12 ${meta.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
              <Icon className={`w-6 h-6 ${meta.iconColor}`} />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">{channel.label}</p>
              <p className="text-sm font-semibold text-gray-800 truncate">{channel.value}</p>
              {channel.description && (
                <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{channel.description}</p>
              )}
            </div>

            {channel.cta && (
              <a
                href={href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="shrink-0 flex items-center gap-1.5 text-brand-primary text-sm font-semibold hover:gap-2.5 transition-all duration-150 whitespace-nowrap"
              >
                {channel.cta}
                <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}
